import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useChatsPage } from "../../api/api-hooks";
import { createColumnHelper } from "@tanstack/react-table";
import ReactTanstackTableUi from "../../common/ReactTanstackTableUi";
// import { FaTrashAlt, FaEye } from "react-icons/fa";
import { formatDateTimeAndYear } from "../../utils/date-utils";
import { useAdminData } from "../../lib/useAdminData";
import { useMemo } from "react";
import {
  filterDataByDate,
  getDateRangeFromSelectedValue,
} from "../../common/Constants";
import useCommonStore from "../../store/CommonStore";
import { usePagination } from "../hooks/usePagination";

function prepareColumns(columnHelper: any, handleActions: any) {
  return [
    columnHelper.accessor((row: any) => row.chatListName, {
      id: "List Name",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        const fetchId = row?.original?.id;

        return (
          <div
            className=" flex flex-col gap-1"
            onClick={() => handleActions("view", fetchId)}
          >
            <div className="font-semibold text-gray-800 text-base truncate w-full max-w-xs underline cursor-pointer">
              {original?.chatListName || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Name</div>,
    }),
    columnHelper.accessor((row: any) => row.createdat, {
      id: "Created At",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-base max-w-xs">
              {original?.timestamp
                ? (() => {
                    const { date, time } = formatDateTimeAndYear(
                      original.timestamp
                    );
                    return `${date} ${time}`;
                  })()
                : "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Created At</div>,
    }),

    columnHelper.accessor((row: any) => row.length, {
      id: "length",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-base max-w-xs">
              {original?.length || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Total Prompts</div>,
    }),

    columnHelper.accessor((row: any) => row.duration, {
      id: "duration",
      cell: (info: any) => {
        const duration = info.getValue();

        if (!duration) return "--";

        const { hours, minutes, seconds } = duration;
        return (
          <div className="font-semibold text-gray-800 text-base">
            {`${hours}h ${minutes}m ${seconds}s`}
          </div>
        );
      },
      header: () => <div>Chat Duration</div>,
    }),

    columnHelper.accessor((row: any) => row.createdBy, {
      id: "Created By",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-base max-w-xs">
              {original?.createdBy || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Created By</div>,
    }),

    columnHelper.accessor((row: any) => row.status, {
      id: "Status",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        return (
          <div className=" flex flex-col gap-1">
            <div className="capitalize font-semibold text-gray-800 text-base max-w-xs">
              {original?.status || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Status</div>,
    }),
    // columnHelper.accessor((row: any) => row.actions, {
    //   id: "Actions",
    //   cell: (info: any) => {
    //     const row = info.row;
    //     const original = row?.original;

    //     return (
    //       <div className="flex items-center justify-center">
    //         {/* View Icon */}
    //         <FaEye
    //           className="text-gray-500 cursor-pointer"
    //           onClick={() => handleActions("view", original?.id)}
    //           title="View"
    //         />

    //         {/* Delete Icon
    //         <FaTrashAlt
    //           className="text-red-500 cursor-pointer"
    //           onClick={() => handleActions("delete", original?.id)}
    //           title="Delete"
    //         /> */}
    //       </div>
    //     );
    //   },
    //   header: () => <div>Actions</div>,
    // }),
  ];
}

function ChatListTable() {
  // const {
  //   data: chats,
  //   isLoading: isChatsLoading,
  //   // error: chatsError,
  // } = useChats();

  const { selectedValue, selectedAgent, selectedUser } = useCommonStore();

  const dateRange = getDateRangeFromSelectedValue(selectedValue);

  // const { startDate, endDate } = dateRange!;

  const { pageIndex, pageSize, getPaginationInfo } = usePagination();

  const [searchParams] = useSearchParams();
  const agentParam = searchParams.get("agent");

  const appName = agentParam === "true" ? "HR App" : "rolesgpt";

  const titleChats = agentParam === "true" ? "HR Agents" : "Chats";

  const agentName = agentParam === "true" ? selectedAgent?.value : "";

  const isAllData = dateRange?.isAllData ?? false;
  const startDate = isAllData ? undefined : dateRange?.startDate ?? "";
  const endDate = isAllData ? undefined : dateRange?.endDate ?? "";

  const {
    data: chats,
    isLoading: isChatsLoading,
    // error: chatsError,
  } = useChatsPage(
    pageIndex + 1,
    pageSize,
    startDate,
    endDate,
    appName,
    agentName,
    isAllData,
    selectedUser
  );

  const { activeChats } = useAdminData();

  const apiData = Array.isArray(chats?.items) ? chats?.items : [];

  const columnHelper = createColumnHelper();

  const columns = prepareColumns(columnHelper, handleActions);

  const navigate = useNavigate();

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const statusFilter = queryParams.get("status");

  const filteredChats = statusFilter === "active" ? activeChats : apiData;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { filteredData } = useMemo(() => {
    return filterDataByDate(filteredChats, selectedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredChats, selectedValue]);

  const paginationInfo = getPaginationInfo(chats);

  //   const [selectedId, setSelectedId] = useState<string | null>(null);

  //   function handleView(fetchId: string) {}

  //   function handleFetch(fetchType: string, fetchId: string) {
  //     // setType(fetchType);
  //     setSelectedId(fetchId);
  //   }

  // function handleActions(fetchType: string, fetchId: string) {
  //   if (!isChatsLoading && fetchType === "view") {
  //     navigate(`/chat/${fetchId}`);
  //   }
  //   if (!isChatsLoading && fetchType === "delete") {
  //     // console.log("delete");
  //   }
  // }

  function handleActions(fetchType: string, fetchId: string) {
    if (!isChatsLoading && fetchType === "view") {
      if (agentParam === "true") {
        navigate(`/agent/${fetchId}`);
      } else {
        navigate(`/chat/${fetchId}`);
      }
    }
  }

  // if (isChatsLoading) {
  //   return <Loader />;
  // }

  return (
    <div className=" ">
      <ReactTanstackTableUi
        // data={filteredData}
        // columns={columns}
        // pagination={apiData?.length > 10}
        // noDataMessage="Chats"
        // tableTitle="Chats"

        data={apiData}
        columns={columns}
        // pagination={apiData?.length > 10}
        noDataMessage="Chats"
        tableTitle={titleChats}
        disableRowHeight={true}
        pagination={true}
        externalPagination={paginationInfo}
        isLoading={isChatsLoading}
        isAgentDropdown={agentParam}
        isChat={true}
      />
    </div>
  );
}

function Chats() {
  return (
    <div className=" flex flex-col gap-6 ">
      <div>
        <ChatListTable />
      </div>
    </div>
  );
}

export default Chats;
